import { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import { Modal } from '../../ui/ModalProject/Modal';
import { Button } from '../../ui/Button/Button';
import { GoodCard } from '../GoodCard/GoodCard';
import { IconClose, IconPlus } from '../../../assets/icons/icons';
import type { PositionDraft } from '../../../types/project';
import { pluralWithCount } from '../../../utils/format';
import styles from './ManualPositionsModal.module.scss';
import React from 'react';

interface ManualPositionsModalProps {
    submitAsRestart?: boolean;
    busy?: boolean;
    open: boolean;
    seed: PositionDraft | null;
    onClose: () => void;
    onSubmit: (drafts: PositionDraft[]) => void;
    onValidationError: (message: string) => void;
    editing?: boolean;
    projectRegion?: string;
}

/** Пустая карточка товара. */
export function emptyDraft(region = ''): PositionDraft {
    return {
        name: '',
        spec: '',
        qty: 1,
        unit: 'шт',
        region,
        priceFrom: 0,
        priceTo: 0,
        requestPrice: null,
        // analogs: true,
        analogs: false,
        logistics: false,
    };
}

interface GoodItem {
    key: number;
    draft: PositionDraft;
}

/** Окно ручного добавления позиций — во весь экран, несколько товаров сразу. */
export const ManualPositionsModal: FC<ManualPositionsModalProps> = ({
    open,
    seed,
    onClose,
    onSubmit,
    onValidationError,
    editing = false,
    projectRegion = '',
    submitAsRestart = false,
    busy = false,
}) => {
    const [goods, setGoods] = useState<GoodItem[]>([]);
    const [invalidKeys, setInvalidKeys] = useState<Set<number>>(new Set());
    const [seq, setSeq] = useState(0);


    const seedRef = useRef(seed);
    seedRef.current = seed;

    const initialRef = useRef<PositionDraft | null>(null);

    useEffect(() => {
        if (!open) {
            setGoods([]);
            setInvalidKeys(new Set());
            return;
        }
        setSeq(1);
        setGoods([{ key: 1, draft: seedRef.current ?? emptyDraft() }]);
        initialRef.current = seedRef.current ?? null;
        setInvalidKeys(new Set());
    }, [open]);

    const changed =
        !initialRef.current ||
        goods.length !== 1 ||
        JSON.stringify(goods[0].draft) !== JSON.stringify(initialRef.current);

    const DIMENSION_KEYS = [
        'length',
        'width',
        'height',
        'weight',
        'transportLength',
        'transportWidth',
        'transportHeight',
        'transportWeight',
    ] as const;

    const diffKeys = (() => {
        const base = initialRef.current;
        const next = goods[0]?.draft;
        if (!base || !next || goods.length !== 1) return [];

        return (Object.keys(next) as (keyof PositionDraft)[]).filter(
            (key) => next[key] !== base[key],
        );
    })();

    const dimsChanged = diffKeys.some((key) =>
        DIMENSION_KEYS.includes(key as (typeof DIMENSION_KEYS)[number]),
    );

    const searchChanged = diffKeys.some(
        (key) => !DIMENSION_KEYS.includes(key as (typeof DIMENSION_KEYS)[number]),
    );

    const dimensionsOnly = editing && submitAsRestart && dimsChanged && !searchChanged;

    const patchGood = (key: number, patch: Partial<PositionDraft>) => {
        setGoods((prev) => prev.map((item) => (item.key === key ? { ...item, draft: { ...item.draft, ...patch } } : item)));
        if (patch.name !== undefined) {
            setInvalidKeys((prev) => {
                if (!prev.has(key)) return prev;
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    };

    const addGood = () => {
        setSeq((prev) => {
            const key = prev + 1;
            setGoods((items) => [...items, { key, draft: emptyDraft(items[0]?.draft.region) }]);
            return key;
        });
    };

    const removeGood = (key: number) => {
        if (goods.length === 1) {
            onValidationError('В форме должен остаться хотя бы один товар');
            return;
        }
        setGoods((prev) => prev.filter((item) => item.key !== key));
    };

    const submit = () => {
        const empty = goods.filter((item) => !item.draft.name.trim());
        if (empty.length) {
            setInvalidKeys(new Set(empty.map((item) => item.key)));
            onValidationError('Заполните наименование товара');
            return;
        }
        onSubmit(
            goods.map((item) => ({
                ...item.draft,
                name: item.draft.name.trim(),
                spec: item.draft.spec.trim(),
                qty: item.draft.qty || 1,
            })),
        );
    };

    return (
        <Modal open={open} onClose={onClose} overlayClassName={styles.overlay} bare>
            <div className={styles.manual} role="dialog" aria-modal="true" aria-label="Новая позиция вручную">
                <div className={styles.head}>
                    <div className={styles.icon}>
                        <IconPlus />
                    </div>
                    <div>
                        <h3>{editing ? 'Изменение позиции' : 'Новая позиция вручную'}</h3>
                        <p>
                            {editing
                                ? 'Обязательно только наименование. Остальное можно оставить как в проекте.'
                                : 'Обязательно только наименование. Можно добавить сразу несколько товаров.'}
                        </p>
                    </div>
                    <div className={styles.spacer} />

                    {!editing && (
                        <span className={styles.counter}>
                            {pluralWithCount(goods.length, 'товар', 'товара', 'товаров')}
                        </span>
                    )}
                    <button type="button" className={styles.close} aria-label="Закрыть окно" onClick={onClose}>
                        <IconClose />
                    </button>
                </div>

                <div className={styles.body}>
                    <div className={styles.goods}>
                        {goods.map((item, index) => (
                            <GoodCard
                                key={item.key}
                                index={index + 1}
                                draft={item.draft}
                                invalid={invalidKeys.has(item.key)}
                                autoFocus={index === goods.length - 1}
                                projectRegion={projectRegion}
                                showDimensions={editing}
                                onChange={(patch) => patchGood(item.key, patch)}
                                onRemove={() => removeGood(item.key)}
                            />
                        ))}
                    </div>

                    {!editing && (
                        <button type="button" className={styles.addMore} onClick={addGood}>
                            <IconPlus />
                            Добавить ещё товар
                        </button>
                    )}
                </div>

                <div className={styles.foot}>
                    <span className={styles.note}>
                        {editing
                            ? dimensionsOnly
                                ? 'Габариты сохранятся, доставка пересчитается. Поиск не запускается'
                                : submitAsRestart
                                    ? dimsChanged
                                        ? 'Поиск запустится заново и определит габариты сам — введённые значения не сохранятся'
                                        : 'Поиск по позиции запустится заново, лимит будет списан'
                                    : 'Изменения применятся к позиции в списке проекта'
                            : 'Позиции появятся в списке проекта. Поиск начнётся только по кнопке «Запустить в работу»'}
                    </span>
                    <div className={styles.footRight}>
                        <Button onClick={onClose}>Отмена</Button>
                        <Button
                            variant="primary"
                            onClick={submit}
                            disabled={busy || (submitAsRestart && !changed)}
                        >
                            {busy && <span className={styles.spinner} aria-hidden="true" />}
                            {busy
                                ? 'Сохраняем…'
                                : editing
                                    ? dimensionsOnly
                                        ? 'Сохранить габариты'
                                        : submitAsRestart
                                            ? 'Перезапустить поиск'
                                            : 'Сохранить'
                                    : 'Добавить в заявку'}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};